import { useEffect, useRef, useState } from 'react';

import { ICheckoutPageSettings } from '../../../../api/checkouts/types/checkout-page.type';
import {
	identifyBuyer,
	trackBehavior
} from '../PageBlocks/behavior-tracker';
import { isPreviewMode } from '../PageBlocks/use-preview-bridge';
import { toast } from '../../../../utils/toast';
import './styles.scss';

/**
 * Exit intent: mouse saindo pelo topo (fechar aba/voltar) → popup de
 * retenção configurado no builder. Uma vez por sessão, só desktop (mobile
 * não tem mouseleave confiável). E-mail capturado identifica o comprador na
 * inteligência de conversão — vira alvo da recuperação de carrinho mesmo sem
 * ter tocado no formulário.
 */
export const ExitIntentPopup = ({
	settings
}: {
	settings?: ICheckoutPageSettings['exitIntent'];
}) => {
	const [open, setOpen] = useState(false);
	const [email, setEmail] = useState('');
	const [sent, setSent] = useState(false);
	const armedRef = useRef(false);

	useEffect(() => {
		if (!settings?.enabled || isPreviewMode()) return;
		const storageKey = `checkout-exit-intent:${window.location.pathname}`;
		try {
			if (sessionStorage.getItem(storageKey)) return;
		} catch {
			// sem storage, segue armado (no máximo mostra de novo)
		}

		// Arma só depois de uns segundos — abrir na entrada é anti-UX.
		const armTimer = window.setTimeout(() => {
			armedRef.current = true;
		}, 5000);

		const onMouseOut = (event: MouseEvent) => {
			if (!armedRef.current) return;
			if (event.relatedTarget) return; // ainda dentro da página
			if (event.clientY > 8) return; // só saída pelo topo
			armedRef.current = false;
			try {
				sessionStorage.setItem(storageKey, '1');
			} catch {
				// segue sem persistir
			}
			setOpen(true);
			trackBehavior('exit_intent_shown', undefined, undefined, {
				once: true
			});
		};

		document.addEventListener('mouseout', onMouseOut);
		return () => {
			window.clearTimeout(armTimer);
			document.removeEventListener('mouseout', onMouseOut);
		};
	}, [settings?.enabled]);

	if (!settings?.enabled || !open) return null;

	const applyCoupon = () => {
		if (!settings.couponCode) return;
		// O campo de cupom escuta este evento e aplica como se digitado.
		window.dispatchEvent(
			new CustomEvent('waid:apply-coupon', {
				detail: settings.couponCode
			})
		);
		setOpen(false);
	};

	const submitEmail = () => {
		const value = email.trim().toLowerCase();
		if (!value.includes('@')) {
			toast.error('Informe um e-mail válido.');
			return;
		}
		identifyBuyer(value);
		trackBehavior('exit_intent_email');
		setSent(true);
		if (settings.couponCode) applyCoupon();
	};

	return (
		<div
			className="exit-intent-backdrop"
			role="dialog"
			aria-modal="true"
			onClick={() => setOpen(false)}
		>
			<div
				className="exit-intent-card"
				onClick={(event) => event.stopPropagation()}
			>
				<button
					type="button"
					className="exit-intent-close"
					aria-label="Fechar"
					onClick={() => setOpen(false)}
				>
					×
				</button>
				<h3>{settings.title || 'Espere! Não vá embora ainda'}</h3>
				<p>
					{settings.message ||
						(settings.couponCode
							? 'Temos um desconto exclusivo para você concluir agora.'
							: 'Deixe seu e-mail e te ajudamos a concluir a compra.')}
				</p>

				{sent ? (
					<p className="exit-intent-success">
						✓ Combinado! Pode continuar sua compra.
					</p>
				) : (
					<>
						{settings.captureEmail !== false && (
							<input
								type="email"
								inputMode="email"
								placeholder="Seu melhor e-mail"
								value={email}
								onChange={(event) =>
									setEmail(event.target.value)
								}
							/>
						)}
						<button
							type="button"
							className="exit-intent-cta"
							onClick={
								settings.captureEmail !== false
									? submitEmail
									: applyCoupon
							}
						>
							{settings.buttonLabel ||
								(settings.couponCode
									? 'Quero meu desconto'
									: 'Continuar minha compra')}
						</button>
					</>
				)}
			</div>
		</div>
	);
};
